import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import CustomerBillingReceipt from "./CustomerBillingReceipt";
import { customFetch } from '../apiClient';
import Billpopup from "./Billpopup";

// ✅ UPDATED: Matching ExPDTForm Compact Styles
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '26px',
    height: '26px',
    fontSize: '11px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    padding: '0'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px', height: '24px' }),
  indicatorsContainer: (base) => ({ ...base, height: '24px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px' }),
  menu: (base) => ({ ...base, zIndex: 99999, borderRadius: '4px' }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    padding: '6px 10px',
    backgroundColor: state.isSelected
      ? 'var(--theme-color, #0088cc)'
      : state.isFocused
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)'
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};

const BillList = () => {
  const [billingSummaries, setBillingSummaries] = useState([]);
  const [searchBillNo, setSearchBillNo] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [billingDetails, setBillingDetails] = useState([]);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [billDataList, setBillDataList] = useState([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [editBillNo, setEditBillNo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const token = sessionStorage.getItem("token");

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const refreshReceiptData = async (billNo) => {
    try {
     const res = await customFetch(`/Billing/search?billNo=${encodeURIComponent(billNo)}`);
      if (!res.ok) return;
      const data = await res.json();
      
      if (data.length === 0) return;
      const dto = data[0];
      setReceiptData({
        customer: dto.customer,
        summary: dto.summary,
        details: dto.details,
      });
    } catch (e) {
      console.error("Failed to refresh receipt data", e);
    }
  };

  const refreshSummary = async (billNo) => {
    try {
    const res = await customFetch(`/Billing/search?billNo=${encodeURIComponent(billNo)}`);
      if (!res.ok) return;
      const data = await res.json();
      const updatedSummary = data[0]?.summary;
      if (!updatedSummary) return;

      setBillingSummaries((prev) =>
        prev.map((s) => (s.billNo === billNo ? updatedSummary : s))
      );
    } catch (e) {
      console.error("Failed to refresh summary", e);
    }
  };

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    if (hospitalId) {
   customFetch(`/Company/${hospitalId}`)
        .then((res) => res.json())
        .then(setCompanyDetails)
        .catch(console.error);
    }
  }, [token]);

  const loadCombinedSearchOptions = async (inputValue) => {
    if (!inputValue) return [];

    try {
   const res = await customFetch(`/Billing/search?billNo=${encodeURIComponent(inputValue)}`);

      if (!res.ok) return [];

      const data = await res.json();
      return data.map((dto) => ({
        label: dto.summary.billNo,
        value: dto.summary.billNo,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const handleCombinedSearchChange = async (selected) => {
    if (!selected) {
      setSearchBillNo(null);
      setBillingSummaries([]);
      setCustomer(null);
      setBillingDetails([]);
      setBillDataList([]);
      return;
    }

    setSearchBillNo(selected);
    setLoading(true);
    setError("");

    try {
    const res = await customFetch(`/Billing/search?billNo=${selected.value}`);

      if (!res.ok) throw new Error("Failed to load billing data");

      const data = await res.json();
      const dto = data[0];

      setBillDataList(data);
      setBillingSummaries([dto.summary]);
      setCustomer(dto.customer);
      setBillingDetails(dto.details);
    } catch (e) {
      setError(e.message || "Error loading data");
      console.error("Load Error", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClickSummary = async (billNo) => {
   if (!window.confirm("Are you sure you want to delete this billing record?")) return;

    try {
      const res = await customFetch(`/BillSummary/${billNo}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Failed to delete summary");

      setBillingSummaries([]);
      setSuccess("Bill deleted successfully!");
    } catch (e) {
      setError(e.message || "Error deleting bill");
    }
  };

  const handlePreviewClick = async () => {
    if (!billDataList.length) return;
    const billNo = billDataList[0].summary.billNo;

    try {
     const res = await customFetch(`/Billing/search?billNo=${encodeURIComponent(billNo)}`);
      if (!res.ok) throw new Error("Failed to fetch receipt data");

      const data = await res.json();
      if (data.length === 0) throw new Error("No data found");

      const dto = data[0];

      setReceiptData({
        customer: dto.customer,
        summary: dto.summary,
        details: dto.details,
      });
      setShowReceipt(true);
    } catch (error) {
      setError(error.message || "Error loading receipt");
      console.error("Error loading receipt data:", error);
    }
  };

  const handleEditIconClick = (e, billNo) => {
    e.stopPropagation();
    
    const popupWidth = Math.min(900, window.innerWidth * 0.9);
    const popupHeight = Math.min(700, window.innerHeight * 0.9);
    
    let centerX = (window.innerWidth - popupWidth) / 2 + window.scrollX;
    let centerY = (window.innerHeight - popupHeight) / 2 + window.scrollY;
    
    centerX = Math.max(10, Math.min(centerX, window.innerWidth - popupWidth - 10 + window.scrollX));
    centerY = Math.max(20, Math.min(centerY, window.innerHeight - popupHeight - 20 + window.scrollY));
    
    setPopupPosition({ x: centerX, y: centerY });
    setEditBillNo(billNo);
  };

  const formatNumber = (num) =>
    typeof num === "number" ? num.toFixed(2) : "0.00";

  return (
    <>
      <div className="dc-wrap">
        <div className="dc-card">
          {/* Header */}
          <div className="dc-header">
            <div className="dc-header-inner">
              <div className="dc-header-brand">
                <div className="dc-header-icon"><i className="bi bi-receipt-cutoff"></i></div>
                <span className="dc-header-title">Billing Summary</span>
              </div>
            </div>
          </div>

          <div className="dc-body">
            {/* Alerts */}
            {error && <div className="dc-alert dc-alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
            {success && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

            {/* Search Section */}
            <div className="dc-section">
              <div className="dc-section-title"><i className="bi bi-search"></i><span>Search Billing Record</span></div>
              <div className="dc-section-body" style={{ padding: '8px 10px' }}>
                <div style={{ maxWidth: '400px' }}>
                  <AsyncSelect
                    cacheOptions
                    loadOptions={loadCombinedSearchOptions}
                    onChange={handleCombinedSearchChange}
                    value={searchBillNo}
                    placeholder="Type bill number..."
                    isClearable
                    styles={customSelectStyles}
                    isLoading={loading}
                    menuPortalTarget={document.body}
                    classNamePrefix="react-select"
                  />
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="dc-section" style={{ marginBottom: '0' }}>
              <div className="dc-section-body" style={{ padding: '0' }}>
                {loading ? (
                  <div className="loading-state">
                    <span className="spinner-border spinner-border-sm me-2"></span> Loading...
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Mobile No</th>
                          <th>Bill No</th>
                          <th>Date</th>
                          <th className="text-right">Total</th>
                          <th className="text-right">Received</th>
                          <th className="text-right">Cash</th>
                          <th className="text-right">UPI</th>
                          <th className="text-right">Due</th>
                          <th className="text-right">Discount (₹)</th>
                          <th>Authority</th>
                          <th>Requested By</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingSummaries.length === 0 ? (
                          <tr>
                            <td colSpan="13" className="empty-state">
                              <i className="bi bi-inbox"></i>
                              <p>No billing records found.</p>
                            </td>
                          </tr>
                        ) : (
                          billingSummaries.map((summary) => (
                            <tr key={summary.billNo}>
                              <td className="fw-medium">{customer?.custName || "-"}</td>
                              <td>{customer?.mobileNo || "-"}</td>
                              <td className="fw-medium text-primary">{summary.billNo}</td>
                              <td className="text-muted">{new Date(summary.billDate).toLocaleDateString()}</td>
                              <td className="text-right">₹{formatNumber(summary.total)}</td>
                              <td className="text-right">₹{formatNumber(summary.received)}</td>
                              <td className="text-right">₹{formatNumber(summary.cash)}</td>
                              <td className="text-right">₹{formatNumber(summary.upi)}</td>
                              <td className="text-right fw-medium" style={{ color: summary.due > 0 ? '#dc2626' : '#15803d' }}>
                                ₹{formatNumber(summary.due)}
                              </td>
                              <td className="text-right">₹{formatNumber(summary.dis)}</td>
                              <td className="text-muted">{summary.disAuthority || "-"}</td>
                              <td className="text-muted">{summary.requestedBy || "-"}</td>
                              <td className="text-center">
                                <div className="btn-group-actions">
                                  <button className="btn-icon btn-preview" onClick={handlePreviewClick} title="Preview Receipt">
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  <button className="btn-icon btn-edit" onClick={(e) => handleEditIconClick(e, summary.billNo)} title="Edit">
                                    <i className="bi bi-pencil-square"></i>
                                  </button>
                                  <button className="btn-icon btn-delete" onClick={() => handleDeleteClickSummary(summary.billNo)} title="Delete">
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Popup */}
      {editBillNo && (
        <Billpopup
          billNo={editBillNo}
          onClose={() => setEditBillNo(null)}
          position={popupPosition}
          onUpdateSuccess={() => {
            refreshSummary(editBillNo);
            refreshReceiptData(editBillNo);
            setShowReceipt(true);
            setEditBillNo(null);
            setSuccess("Bill updated successfully!");
          }}
        />
      )}

      {/* Receipt Modal */}
      {showReceipt && receiptData && companyDetails && (
        <CustomerBillingReceipt
          show={showReceipt}
          receiptData={receiptData}
          companyDetails={companyDetails}
          onClose={() => setShowReceipt(false)}
          onPrint={() => window.print()}
        />
      )}

      {/* Styles */}
      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .dc-wrap { padding: 0; font-family: system-ui, sans-serif; }

        .dc-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; overflow: hidden; }

        .dc-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-radius: 6px 6px 0 0; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .dc-header-inner { display: flex; align-items: center; justify-content: space-between; }
        .dc-header-brand { display: flex; align-items: center; gap: 6px; color: white; font-weight: 600; font-size: 13px; }
        .dc-header-icon { display: flex; align-items: center; justify-content: center; font-size: 14px; }

        .dc-body { padding: 10px 12px; }

        .dc-section { background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 5px; margin-bottom: 10px; overflow: visible; }
        .dc-section-title { background: var(--theme-color, #0088cc); padding: 5px 10px; display: flex; align-items: center; gap: 5px; border-radius: 4px 4px 0 0; color: white; font-size: 11px; font-weight: 600; }
        .dc-section-body { background: #fff; border-radius: 0 0 5px 5px; }

        .dc-alert { padding: 6px 10px; border-radius: 4px; font-size: 11px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .dc-alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .dc-alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }

        .loading-state { text-align: center; padding: 20px; color: var(--theme-color, #0088cc); font-size: 11px; font-weight: 500; }

        .table-responsive { overflow-x: auto; border-radius: 0 0 5px 5px; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; }

        .custom-table thead { background: #f1f5f9; border-bottom: 1px solid #cbd5e0; }
        .custom-table thead th { 
          color: #2c3e50; font-weight: 600; padding: 6px 8px; 
          font-size: 10px; text-transform: uppercase; white-space: nowrap; 
          text-align: left; border: 1px solid #e0e6ed;
        }
        .custom-table thead th.text-right { text-align: right; }
        .custom-table thead th.text-center { text-align: center; }

        .custom-table tbody tr { border-bottom: 1px solid #e0e6ed; transition: background 0.2s; }
        .custom-table tbody tr:hover { background: #f8fafc; }
        .custom-table tbody td { padding: 4px 8px; vertical-align: middle; border-right: 1px solid #e0e6ed; white-space: nowrap; }
        .custom-table tbody td:last-child { border-right: none; }

        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .fw-medium { font-weight: 600; }
        .text-muted { color: #64748b; }
        .text-primary { color: var(--theme-color, #0088cc) !important; }

        .empty-state { text-align: center; padding: 30px 20px !important; color: #94a3b8; }
        .empty-state i { font-size: 32px; margin-bottom: 8px; display: block; opacity: 0.5; }
        .empty-state p { margin: 0; font-size: 11px; }

        .btn-icon {
          width: 22px; height: 22px; padding: 0; display: inline-flex; align-items: center; justify-content: center;
          border-radius: 3px; border: none; cursor: pointer; transition: all 0.2s; font-size: 11px;
        }

        .btn-preview { background: #fef08a; color: #854d0e; }
        .btn-preview:hover { background: #eab308; color: white; }

        .btn-edit { background: #e0f2fe; color: #0284c7; }
        .btn-edit:hover { background: #0284c7; color: white; }

        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; }

        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }
      `}</style>
    </>
  );
};

export default BillList;